import EventsView from '@components/event/EventsView'
import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import Box from '@components/base/Box'
import { useTheme } from '@libs/useTheme'
import Text from '@components/base/Text'
import { UnauthenticatedWrapperNoFooter } from '@layouts/Wrapper'
import { GET_PUBLIC_EVENTS_BY_ORGANISATION_URL } from '@graphql/events/getPublicEventsByOrganisationUrl'
import { useOrganisationProfile } from '@libs/useOrganisationProfile'
import headerBackground from '@public/header-background.png'
import LoadingSpinner from '@components/base/LoadingSpinner'
import LoadingBar from '@components/base/LoadingBar'
import Link from 'next/link'
import { GET_PREMIUM_EVENTS_BY_ORGANISATION_URL } from '@graphql/events/getPremiumEventsByOrganisationUrl'
import { Event, Organisation } from '@graphql/__generated/graphql'
import { Button } from '@components/inputs/Button'
import { useAuth } from '@libs/Auth/useAuth'
import { FaStar } from 'react-icons/fa'
import Tooltip from '@components/tootilp/Tooltip'
import moment from 'moment'
import { GetServerSideProps } from 'next'
import { EventMetadata } from 'pages/event/[eventId]'

export default function PublicDashboard() {
  const { theme } = useTheme()
  const { getPermission } = useAuth()
  const isDarkTheme = theme.type === 'DARK'
  const router = useRouter()
  const { publicDashboard } = router.query

  const { loading, data } = useQuery(GET_PUBLIC_EVENTS_BY_ORGANISATION_URL, {
    variables: { url: publicDashboard as string },
    skip: !publicDashboard,
  })
  const { loading: premiumLoading, data: premiumData } = useQuery(
    GET_PREMIUM_EVENTS_BY_ORGANISATION_URL,
    {
      variables: { url: publicDashboard as string },
      skip: !publicDashboard,
    }
  )

  const {
    loading: organisationLoading,
    profile: organisationData,
    ready: organisationReady,
  } = useOrganisationProfile(publicDashboard as string)

  const headerBanner = organisationData?.bannerImage ?? headerBackground.src

  const headerTitle = organisationData?.headerTextOne ? (
    organisationData?.headerTextOne
  ) : (
    <>
      Welcome to your <br />
      {organisationData?.name} Space
    </>
  )

  if (!organisationReady || organisationLoading) {
    return (
      <Box className="flex-1 overflow-y-scroll mt-20 scrollbar-hide h-[calc(100vh_-_81px)] relative flex items-center flex-col">
        <LoadingBar />
      </Box>
    )
  }

  const headerSubTitle = organisationData?.headerTextTwo ? (
    organisationData?.headerTextTwo
  ) : (
    <>
      Here you’ll discover all the events you’ve signed up to along with the
      events you’ve created.
    </>
  )

  const sortEvents = (events: Event[]) => {
    if (!events) return []
    return [...events].sort((a, b) => {
      const dateA = a?.event?.startDateTime
        ? new Date(a?.event?.startDateTime)
        : null
      const dateB = b?.event?.startDateTime
        ? new Date(b?.event?.startDateTime)
        : null

      if (!dateA && !dateB) return 0 // Both dates are null or undefined
      if (!dateA) return 1 // dateA is null or undefined, so it should come after dateB
      if (!dateB) return -1 // dateB is null or undefined, so dateA should come before dateB

      return dateA.getTime() - dateB.getTime()
    })
  }

  const events = sortEvents(
    data?.getPublicEventsByOrganisationUrl as Event[]
  ).filter((f) => moment().isBefore(moment(f.event?.endDateTime)))
  const premiumEvents = sortEvents(
    premiumData?.getPremiumEventsByOrganisationUrl as Event[]
  ).filter((f) => moment().isBefore(moment(f.event?.endDateTime)))

  const isMember = getPermission('USER::MEMBERSHIP::*')?.includes(
    organisationData?.name!
  )

  return (
    <Box className="flex-1 overflow-y-scroll overflow-x-hidden scrollbar-hide h-[calc(100vh_-_81px)] relative flex items-center flex-col pb-10">
      <Box
        className="relative sm:flex justify-center w-full md:min-h-[25rem]"
        style={{
          backgroundImage: `url(${headerBanner})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: theme.backgroundColour,
        }}
      >
        <Box className="lg:min-w-[57rem] z-999 relative w-full flex justify-center items-center ">
          <Box
            className={`rounded-2xl shadow-lg md:w-[85%] max-w-[1500px] flex p-10 md:p-6 md:px-8 md:py-4 md:pt-6 md:pb-6 z-1000 animate-text-focus-in ${
              isDarkTheme ? '' : 'border border-forumm-menu-border'
            }`}
            color="backgroundColorBanner"
            style={{
              background: isDarkTheme
                ? 'rgba(0, 0, 0, 0.7)'
                : 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
            }}
          >
            <Box className="px-6 lg:px-0 z-1000 relative w-full">
              <Text className="text-4xl pb-4 text-center leading-snug mt-2 px-12 sm:px-0 mb-2">
                {headerTitle}
              </Text>
              <Box
                className="animate-text-focus-in"
                style={{ animationDelay: '0.8s' }}
              >
                <Text className="text-base text-center px-12 sm:px-6 animate-text-focus-in">
                  {headerSubTitle}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className="w-3/4 flex-col max-w-[1400px]">
        {premiumEvents && premiumEvents.length > 0 && (
          <Box className="flex items-center mb-2 mt-8 pl-5 sm:pl-0 ml-[8%]">
            <Text className="text-2xl">Premium Events</Text>
            <Tooltip
              className="cursor-help"
              tooltip={
                isMember ? 'Already a member.' : 'Become a member today!'
              }
            >
              <FaStar className="mx-2" fill={isMember ? 'gold' : 'gray'} />
            </Tooltip>
            <Button
              title="Sign up"
              type="special"
              show={!isMember}
              onClick={() =>
                router.push(`/membership/${organisationData.name}`)
              }
            />
          </Box>
        )}
        <Box className="items-center flex flex-row">
          {!premiumLoading && premiumEvents && premiumEvents.length > 0 && (
            <EventsView
              events={premiumEvents as any}
              types={['ONLINE', 'IN_PERSON', 'FUNDRAISER']}
              title={''}
              currency={organisationData.currency ?? 'GBP'}
              justify="center"
            />
          )}
        </Box>

        {events && (
          <Text className="text-2xl mb-2 mt-8 pl-5 sm:pl-0 ml-[8%]">
            Public Events
          </Text>
        )}
        <Box className="items-center flex flex-row">
          {!loading && events && (
            <EventsView
              events={events as any}
              types={['ONLINE', 'IN_PERSON', 'FUNDRAISER']}
              title={''}
              currency={organisationData.currency ?? 'GBP'}
              justify="center"
            />
          )}
          {(premiumLoading || loading) && (
            <Box className="flex justify-center w-full py-32">
              <LoadingSpinner size="medium" />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const orgNameQuery =
      context.params?.publicDashboard ??
      (context.query.publicDashboard as string)

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL
      if (!apiUrl) throw new Error('No API URL set!')

      const org: Partial<Organisation> = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
  query getOrganisationPublicInfoByName($name: String!) {
    getOrganisationByName(name: $name) {
      name
      bannerImage
      logoImage
      mainColour
      currency
      headerTextOne
      headerTextTwo
      url
    }
  }
      `,
          variables: {
            name: orgNameQuery,
          },
        }),
      })
        .then((res) => res.json())
        .then((res) => {
          console.log(res)
          return res
        })
        .then((res) => {
          if (res.data.getOrganisationByName)
            return res.data.getOrganisationByName
        })
      const orgName = org.name
      const domain = process.env.NEXT_PUBLIC_DOMAIN as string
      const pageUrl = `https://${domain.includes('qa') || domain.includes('app') ? '' : 'app.'}${domain}/org/${orgNameQuery}`

      const payload: EventMetadata = {
        title: `${org.name} on Forumm` ?? 'Forumm',
        description:
          org.headerTextOne ?? 'Event management and fundraising made simple.',
        image:
          org.logoImage ??
          'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/f97ae674-f3d9-497e-bad7-db680ac3e832.png',
        url: pageUrl,
      }

      return {
        props: {
          orgName: orgName,
          metadata: payload,
        },
      }
    } catch (err) {
      console.log({ err })

      return {
        props: {
          orgName: orgNameQuery,
        },
      }
    }
  } catch (err) {
    console.log({ err })

    return {
      props: {},
    }
  }
}

PublicDashboard.Layout = UnauthenticatedWrapperNoFooter
